'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo } from 'react';

import '@bytel/product-wall/style';
import {
    Container,
    Icon,
    IconName,
    IconSize,
    Row,
    Rows,
    Section,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { useOnScreen } from '@hooks/use-on-screen';

import { getStoredComparator, storeComparator } from '@helpers/comparator';
import { replaceWindowUrl } from '@helpers/document';
import { paths } from '@helpers/path';
import { sendPageViewTracking } from '@helpers/tracking';

import { Comparator, ComparatorCell, ComparatorColumns, ComparatorRows } from '@comparator/components';
import {
    MobileProductsCharacteristicsType,
    buildCharacteristicGroups,
    sendRemoveProductTracking,
} from '@comparator/helpers';

import { MobileComparatorHeader } from './mobile-comparator-header';
import { MobileComparatorLine } from './mobile-comparator-line';

interface ComparatorProps {
    productsCharacteristics: MobileProductsCharacteristicsType;
    isRenewal: boolean;
}

export function MobileComparator({ productsCharacteristics, isRenewal }: Readonly<ComparatorProps>) {
    const searchParams = useSearchParams();
    const { ref, isVisible } = useOnScreen(true);
    const router = useRouter();

    const handleRemoveProduct = (gencodeToRemove: string) => {
        const urlKeys = searchParams.get('produits')?.split(',') ?? [];
        const updatedKeys = urlKeys.filter((gencode) => gencode !== gencodeToRemove);

        removeStoredProduct(gencodeToRemove);
        sendRemoveProductTracking();

        if (updatedKeys.length === 0) {
            return router.push(paths.phoneWall);
        }

        const currentUrl = location.origin + location.pathname;
        const newSearchParams = new URLSearchParams({ produits: updatedKeys.join(',') }).toString();

        replaceWindowUrl(`${currentUrl}?${newSearchParams}`);
    };

    const removeStoredProduct = (gencodeToRemove: string) => {
        const filteredProducts = getStoredComparator().filter((product) => product.gencode !== gencodeToRemove);

        storeComparator(filteredProducts);
    };

    const products = useMemo(() => {
        const urlKeys = searchParams.get('produits')?.split(',') ?? [];
        return productsCharacteristics.filter((product) => urlKeys.includes(product.gencode));
    }, [productsCharacteristics, searchParams]);

    const characteristicGroups = useMemo(
        () => buildCharacteristicGroups(isRenewal, products, handleRemoveProduct, ref),
        [products, handleRemoveProduct],
    );

    useEffect(() => {
        sendPageViewTracking(false, {
            page: 'Comparateur_telephones',
            pageCategory: 'Liste_produits_telephone',
            universe: 'Boutique_mobile',
            products: productsCharacteristics.map((product) => ({
                name: product.name,
                manufacturer: product.brand,
                gencode: product.gencode,
                price: product.price,
                type_id: product.typeId,
            })),
        });
    }, [productsCharacteristics]);

    return (
        <>
            <MobileComparatorHeader />
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container>
                    <Rows>
                        {!isVisible && characteristicGroups[0] && (
                            <Row className='is-sticky-top has-background-white'>
                                <ComparatorColumns>
                                    {...products.map((p) => (
                                        <ComparatorCell key={p.name}>
                                            <div className='is-relative'>
                                                <Icon
                                                    name={IconName.TIMES}
                                                    size={IconSize.SMALL}
                                                    className='is-absolute-top-right cursor-pointer'
                                                    onClick={() => handleRemoveProduct?.(p.gencode)}
                                                />
                                                <Title
                                                    level={TitleLevels.FIVE}
                                                    typo={TypographyAlign.TEXT_CENTERED}
                                                    marginless
                                                >
                                                    {p.brand}
                                                </Title>
                                                <Title level={TitleLevels.FIVE} typo={TypographyAlign.TEXT_CENTERED}>
                                                    {p.name}
                                                </Title>
                                            </div>
                                        </ComparatorCell>
                                    ))}
                                    {...Array(Math.max(0, 3 - products.length))
                                        .fill(null)
                                        .map((_, index, array) => (
                                            <ComparatorCell
                                                key={`placeholder-${index}`}
                                                className={index === array.length - 1 ? 'is-hidden-mobile' : ''}
                                            >
                                                &nbsp;
                                            </ComparatorCell>
                                        ))}
                                </ComparatorColumns>
                            </Row>
                        )}
                        <Row>
                            <Comparator>
                                {characteristicGroups.map(({ name, characteristics }) => (
                                    <ComparatorRows key={name}>
                                        <MobileComparatorLine title={name} characteristics={characteristics} />
                                    </ComparatorRows>
                                ))}
                            </Comparator>
                        </Row>
                    </Rows>
                </Container>
            </Section>
        </>
    );
}
